import { useState } from "react";
import { styled } from "@pigment-css/react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslations } from "@acto/web-common/locales";
import { useGenerateApiKey } from "@acto/taskpane/core/data-access/api-keys";
import { ApiKeyScope } from "@acto/gql/graphql";
import { Flex } from "../../ui/primitives/Flex";

interface GenerateApiKeyDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerated: () => void;
}

const Overlay = styled(Dialog.Overlay)({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  zIndex: 10010,
});

const Content = styled(Dialog.Content)(({ theme }) => ({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: theme.managementView.colors.neutral[0],
  borderRadius: theme.managementView.radius[16],
  boxShadow: theme.shadows[3],
  padding: theme.managementView.spacing[24],
  zIndex: 10011,
  width: "480px",
  maxWidth: "90vw",
  maxHeight: "90vh",
  overflow: "auto",
}));

const Title = styled(Dialog.Title)(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[17],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[90],
  margin: 0,
}));

const Label = styled("label")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
}));

const Input = styled("input")(({ theme }) => ({
  width: "100%",
  height: "40px",
  padding: `0 ${theme.managementView.spacing[12]}`,
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  color: theme.managementView.colors.neutral[90],
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1px solid ${theme.managementView.colors.neutral[24]}`,
  borderRadius: theme.managementView.radius[10],
  outline: "none",
  boxSizing: "border-box",
  "&:focus": {
    borderColor: theme.colors.brand[500],
  },
  "&::placeholder": {
    color: theme.managementView.colors.neutral[56],
  },
}));

const CheckboxRow = styled("label")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  color: theme.managementView.colors.neutral[90],
  cursor: "pointer",
}));

const ButtonGroup = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.managementView.spacing[12],
  marginTop: theme.managementView.spacing[16],
  borderTop: `1px solid ${theme.managementView.colors.neutral[8]}`,
  paddingTop: theme.managementView.spacing[16],
}));

const Button = styled("button")<{ variant?: "primary" | "secondary" }>(({ theme }) => ({
  height: "40px",
  padding: `0 ${theme.managementView.spacing[24]}`,
  borderRadius: "50px",
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  cursor: "pointer",
  transition: "background-color 0.2s",
  variants: [
    {
      props: { variant: "secondary" },
      style: {
        backgroundColor: "transparent",
        border: `1px solid ${theme.managementView.colors.neutral[8]}`,
        color: theme.managementView.colors.neutral[56],
        "&:hover": {
          backgroundColor: theme.managementView.colors.neutral[4],
        },
      },
    },
    {
      props: { variant: "primary" },
      style: {
        backgroundColor: theme.colors.brand[500],
        border: "none",
        color: theme.managementView.colors.neutral[0],
        "&:hover": {
          backgroundColor: theme.colors.brand[600],
        },
        "&:disabled": {
          backgroundColor: theme.managementView.colors.neutral[24],
          cursor: "not-allowed",
        },
      },
    },
  ],
}));

const KeyDisplay = styled("div")(({ theme }) => ({
  fontFamily: "monospace",
  fontSize: theme.managementView.font.size[13],
  color: theme.managementView.colors.neutral[90],
  backgroundColor: theme.managementView.colors.neutral[4],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[10],
  padding: theme.managementView.spacing[12],
  wordBreak: "break-all",
  userSelect: "all",
}));

const Warning = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[13],
  color: theme.managementView.colors.neutral[56],
  margin: 0,
}));

const AVAILABLE_SCOPES = [ApiKeyScope.NotesRead] as const;

export const GenerateApiKeyDialog = ({ open, onClose, onGenerated }: GenerateApiKeyDialogProps) => {
  const t = useTranslations("apiKeys");
  const tCommon = useTranslations("common");
  const { generateApiKey } = useGenerateApiKey();

  const [name, setName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<Set<ApiKeyScope>>(new Set());
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleScopeToggle = (scope: ApiKeyScope) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) {
        next.delete(scope);
      } else {
        next.add(scope);
      }
      return next;
    });
  };

  const handleGenerate = async () => {
    setIsSubmitting(true);
    const result = await generateApiKey({
      name,
      scopes: Array.from(selectedScopes),
    });
    setIsSubmitting(false);

    if (result) {
      setGeneratedKey(result.plainTextKey);
    }
  };

  const handleCopy = async () => {
    if (generatedKey) {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (generatedKey) {
      onGenerated();
    }
    setName("");
    setSelectedScopes(new Set());
    setGeneratedKey(null);
    setCopied(false);
    onClose();
  };

  const isFormValid = name.trim().length > 0 && selectedScopes.size > 0;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <Dialog.Portal>
        <Overlay />
        <Content>
          {generatedKey ? (
            <Flex direction="column" gap={16}>
              <Title>{t("generateDialog.successTitle")}</Title>
              <KeyDisplay>{generatedKey}</KeyDisplay>
              <Warning>{t("generateDialog.copyWarning")}</Warning>
              <ButtonGroup>
                <Button type="button" variant="secondary" onClick={handleCopy}>
                  {copied ? t("generateDialog.copied") : t("generateDialog.copy")}
                </Button>
                <Button type="button" variant="primary" onClick={handleClose}>
                  {t("generateDialog.close")}
                </Button>
              </ButtonGroup>
            </Flex>
          ) : (
            <Flex direction="column" gap={16}>
              <Title>{t("generateDialog.title")}</Title>
              <Flex direction="column" gap={6}>
                <Label>{t("generateDialog.nameLabel")}</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("generateDialog.namePlaceholder")}
                  autoFocus
                />
              </Flex>
              <Flex direction="column" gap={6}>
                <Label>{t("generateDialog.scopesLabel")}</Label>
                {AVAILABLE_SCOPES.map((scope) => (
                  <CheckboxRow key={scope}>
                    <input
                      type="checkbox"
                      checked={selectedScopes.has(scope)}
                      onChange={() => handleScopeToggle(scope)}
                    />
                    {t(`scopes.${scope}`)}
                  </CheckboxRow>
                ))}
              </Flex>
              <ButtonGroup>
                <Button type="button" variant="secondary" onClick={handleClose}>
                  {tCommon("cancel")}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleGenerate}
                  disabled={!isFormValid || isSubmitting}
                >
                  {t("generateDialog.generate")}
                </Button>
              </ButtonGroup>
            </Flex>
          )}
        </Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
